/**
 * Admin-only "Add field for [customer]" full-screen map.
 *
 * Flow:
 *   1. Admin taps anywhere on satellite map to drop a centre pin
 *   2. They choose one of three boundary modes:
 *      a. SNAP — server queries OSM Overpass for the field polygon at that point
 *      b. CORNERS — they tap each corner of the field; polygon updates live
 *      c. CIRCLE — slider 30–500 m radius around the centre pin
 *   3. Name the field, save
 *
 * Used in two places:
 *   - Silage Tracker wizard step 3 (admins only)
 *   - Future /app/admin/fields list page (admins manage all fields)
 */
import { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Polygon, Polyline, Circle, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { opApi } from "@/lib/operator";
import { MapPin, X, Check, Loader2, AlertTriangle } from "lucide-react";

// Crosshair-style marker for the centre pin
const centreIcon = L.divIcon({
  className: "",
  html: `<div style="width:32px;height:32px;border-radius:50%;background:#c41e3a;border:4px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.5)"></div>`,
  iconSize: [32, 32], iconAnchor: [16, 16],
});

// Helper to compute haversine distance in metres
function distM(a, b) {
  const R = 6371000, toRad = d => d * Math.PI / 180;
  const dLat = toRad(b[0] - a[0]), dLon = toRad(b[1] - a[1]);
  const s = Math.sin(dLat/2)**2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLon/2)**2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Build a circle as a polygon (16 sides) so we can save it the same way
function circleAsPolygon(centre, radiusM, sides = 16) {
  const [lat, lon] = centre;
  const out = [];
  const earthR = 6371000;
  for (let i = 0; i < sides; i++) {
    const θ = (2 * Math.PI * i) / sides;
    const dLat = (radiusM * Math.cos(θ)) / earthR;
    const dLon = (radiusM * Math.sin(θ)) / (earthR * Math.cos(lat * Math.PI / 180));
    out.push([lat + dLat * 180 / Math.PI, lon + dLon * 180 / Math.PI]);
  }
  return out;
}

function ClickCatcher({ mode, onCentre, onCorner }) {
  useMapEvents({
    click(e) {
      if (mode === "centre") onCentre([e.latlng.lat, e.latlng.lng]);
      else if (mode === "corners") onCorner([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

function FitView({ centre }) {
  const map = useMap();
  useEffect(() => {
    if (centre) map.setView(centre, 17, { animate: true });
  }, [centre, map]);
  return null;
}

export default function AddFieldMap({ client, defaultCentre, onCancel, onSaved }) {
  const [centre, setCentre] = useState(null);              // [lat, lon] of the field's centre pin
  const [polygon, setPolygon] = useState(null);            // [[lat,lon], ...] closed polygon
  const [polygonSource, setPolygonSource] = useState(null);
  const [corners, setCorners] = useState([]);              // mode-corners staging
  const [radius, setRadius] = useState(120);               // mode-circle, metres
  const [mode, setMode] = useState("centre");              // centre | snap | corners | circle | review
  const [fieldName, setFieldName] = useState("");
  const [snapBusy, setSnapBusy] = useState(false);
  const [snapErr, setSnapErr] = useState(null);
  const [saving, setSaving] = useState(false);

  // Default map centre: roughly Aberdeenshire if nothing supplied
  const initial = defaultCentre || [57.243, -2.495];

  const trySnap = async () => {
    if (!centre) return;
    setSnapBusy(true); setSnapErr(null);
    try {
      const r = await opApi.get(`/app/tracker/snap-field?lat=${centre[0]}&lon=${centre[1]}`);
      if (r.data.polygon && r.data.polygon.length >= 3) {
        setPolygon(r.data.polygon);
        setPolygonSource("osm");
        setMode("review");
      } else {
        setSnapErr(r.data.reason || "No field outline registered at that exact point. Try the corner-tap or circle modes.");
      }
    } catch (e) {
      setSnapErr(e.response?.data?.detail || e.message);
    } finally { setSnapBusy(false); }
  };

  const onCorner = (pt) => {
    setCorners(prev => [...prev, pt]);
  };

  const confirmCorners = () => {
    if (corners.length < 3) return;
    setPolygon([...corners, corners[0]]);    // close the ring
    setPolygonSource("manual");
    setMode("review");
  };

  const confirmCircle = () => {
    if (!centre) return;
    setPolygon(circleAsPolygon(centre, radius));
    setPolygonSource("circle");
    setMode("review");
  };

  const save = async () => {
    if (!centre || !fieldName.trim()) return;
    setSaving(true);
    try {
      const r = await opApi.post("/app/tracker/fields", {
        name: fieldName.trim(),
        lat: centre[0],
        lon: centre[1],
        client_id: client?.id || null,
        client_name: client?.name || null,
        polygon: polygon,
        polygon_source: polygonSource,
      });
      onSaved(r.data);
    } catch (e) {
      window.alert(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[6500] bg-slate-900 flex flex-col" data-testid="add-field-map">
      {/* Top bar */}
      <div className="bg-[#1a1a5c] text-white px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={onCancel} className="p-1.5 -ml-1 rounded hover:bg-white/10" data-testid="add-field-close">
          <X size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/70">Add field</div>
          <div className="font-display font-black text-base truncate">{client?.name || "New field"}</div>
        </div>
      </div>

      {/* Helper banner */}
      <div className="bg-amber-400 text-[#1a1a5c] px-4 py-2 text-xs font-bold flex items-center gap-2 shrink-0">
        {mode === "centre"  && <><MapPin size={14} /> Tap the satellite map at the middle of the field</>}
        {mode === "snap"    && <><Loader2 size={14} className="animate-spin" /> Snapping to OpenStreetMap field boundary…</>}
        {mode === "corners" && <><MapPin size={14} /> Tap each corner of the field ({corners.length} so far). Tap Done when finished.</>}
        {mode === "circle"  && <><MapPin size={14} /> Drag the slider to size the circle around the centre pin.</>}
        {mode === "review"  && <><Check size={14} /> Looks right? Name it and save — or pick a different boundary mode.</>}
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        <MapContainer center={initial} zoom={defaultCentre ? 17 : 14} style={{ height: "100%", width: "100%" }}>
          <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" maxZoom={19} />
          <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}" maxZoom={19} opacity={0.9} />
          <ClickCatcher mode={mode === "corners" ? "corners" : (centre ? null : "centre")}
                        onCentre={setCentre} onCorner={onCorner} />
          {centre && <FitView centre={centre} />}
          {centre && <Marker position={centre} icon={centreIcon} />}
          {polygon && polygon.length >= 3 && (
            <Polygon positions={polygon} pathOptions={{ color: "#10b981", weight: 3, fillOpacity: 0.25 }} />
          )}
          {mode === "corners" && corners.length >= 2 && (
            <Polyline positions={corners} pathOptions={{ color: "#f5a623", weight: 2, dashArray: "6 4" }} />
          )}
          {mode === "corners" && corners.map((c, i) => (
            <Marker key={i} position={c} icon={L.divIcon({ className: "",
              html: `<div style="width:14px;height:14px;border-radius:50%;background:#f5a623;border:2px solid #fff"></div>`,
              iconSize: [14, 14], iconAnchor: [7, 7] })} />
          ))}
          {mode === "circle" && centre && (
            <Circle center={centre} radius={radius} pathOptions={{ color: "#10b981", weight: 2, fillOpacity: 0.2 }} />
          )}
        </MapContainer>
      </div>

      {/* Bottom controls */}
      <div className="bg-white border-t-2 border-slate-200 px-4 py-3 shrink-0 space-y-3 max-h-[45vh] overflow-y-auto">
        {/* Centre-pin → choose boundary mode */}
        {centre && mode !== "review" && (
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => { setMode("snap"); trySnap(); }} disabled={snapBusy} data-testid="bd-snap"
                    className={`rounded-xl py-3 text-xs font-bold border-2 transition ${mode === "snap"
                      ? "bg-amber-100 border-amber-400 text-[#1a1a5c]"
                      : "bg-slate-50 border-slate-200 hover:bg-amber-50 text-[#1a1a5c]"}`}>
              {snapBusy ? <Loader2 className="animate-spin inline mr-1" size={12} /> : null}
              Snap to OSM
            </button>
            <button onClick={() => { setMode("corners"); setCorners([]); setPolygon(null); }} data-testid="bd-corners"
                    className={`rounded-xl py-3 text-xs font-bold border-2 transition ${mode === "corners"
                      ? "bg-amber-100 border-amber-400 text-[#1a1a5c]"
                      : "bg-slate-50 border-slate-200 hover:bg-amber-50 text-[#1a1a5c]"}`}>
              Tap corners
            </button>
            <button onClick={() => { setMode("circle"); setPolygon(null); }} data-testid="bd-circle"
                    className={`rounded-xl py-3 text-xs font-bold border-2 transition ${mode === "circle"
                      ? "bg-amber-100 border-amber-400 text-[#1a1a5c]"
                      : "bg-slate-50 border-slate-200 hover:bg-amber-50 text-[#1a1a5c]"}`}>
              Circle
            </button>
          </div>
        )}

        {/* Snap error fallback hint */}
        {snapErr && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{snapErr}</span>
          </div>
        )}

        {/* Corner-tap controls */}
        {mode === "corners" && (
          <div className="flex gap-2">
            <button onClick={() => setCorners([])} disabled={!corners.length}
                    className="flex-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 text-slate-700 font-bold rounded-lg py-2 text-xs">
              Clear
            </button>
            <button onClick={confirmCorners} disabled={corners.length < 3} data-testid="corners-done"
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-bold rounded-lg py-2 text-xs">
              Done ({corners.length} corners)
            </button>
          </div>
        )}

        {/* Circle radius slider */}
        {mode === "circle" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500 font-bold">Radius</span>
              <span className="text-[#c41e3a] font-bold tabular-nums">{radius} m</span>
            </div>
            <input type="range" min={30} max={500} step={5} value={radius}
                   onChange={e => setRadius(Number(e.target.value))} data-testid="circle-radius"
                   className="w-full accent-[#c41e3a]" />
            <button onClick={confirmCircle} data-testid="circle-done"
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg py-2 text-xs">
              Use this circle
            </button>
          </div>
        )}

        {/* Review + save */}
        {mode === "review" && (
          <div className="space-y-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Field name</span>
              <input value={fieldName} onChange={e => setFieldName(e.target.value)} autoFocus
                     placeholder="e.g. Lower 10" data-testid="field-name-input"
                     className="mt-1 w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2.5 text-[#1a1a5c] focus:outline-none focus:border-[#c41e3a]" />
            </label>
            <div className="flex gap-2">
              <button onClick={() => setMode("centre")} className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg px-4 py-2.5 text-xs">
                Redo
              </button>
              <button onClick={save} disabled={!fieldName.trim() || saving} data-testid="save-field"
                      className="flex-1 bg-[#c41e3a] hover:bg-[#d92a47] disabled:opacity-40 text-white font-bold rounded-lg py-2.5 text-sm flex items-center justify-center gap-2">
                {saving ? <><Loader2 className="animate-spin" size={14} /> Saving…</> : <><Check size={14} /> Save field</>}
              </button>
            </div>
            <div className="text-[10px] text-slate-400 text-center">
              Boundary source: <strong>{polygonSource || "none"}</strong>{polygon ? ` · ${polygon.length} vertices` : ""}
            </div>
          </div>
        )}

        {!centre && (
          <div className="text-[10px] text-center text-slate-500 py-1">
            Pan/zoom the satellite map to find the field, then tap its centre.
          </div>
        )}
      </div>
    </div>
  );
}
